import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentAnnotation, currentPovState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import {
  Annotation,
  FloorMapElement,
  Point,
  SenceData,
  SenceDataResponse
} from 'types/building'
import { Direction, KrpanoInitProps, Stairway } from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'

export default function useViewerKrpano() {
  const { id } = useParams()
  const { t } = useTranslation()
  const [points] = useRecoilState(listPointState)
  const [searchParams] = useSearchParams()
  const urlH = searchParams.get('hlookat')
  const urlV = searchParams.get('vlookat')
  const urlZ = searchParams.get('fov')
  const isAnnotationDisplay = searchParams.get('is_display')
  const isFirstLoading = useRef<boolean>(true)

  const { navigate, getBuiildingType } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentSelected] = useRecoilState(currentAnnotation)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)

  const [currentHgap, setCurrentHgap] = useState(0)

  // function get all data for create scene
  const { trigger: getSenceData } = useMutation(`v1/points/file-info`)

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const { data: mapItems, isValidating: isValidatingMapItems } =
    useSWR<FloorMapElement>(
      currentViewer.selectedFloor
        ? {
            url: 'v1/points',
            params: {
              buildingId: id,
              floorId: currentViewer.selectedFloor
            }
          }
        : null,
      {
        revalidateOnMount: false
      }
    )
  const { trigger, isMutating } = useMutation('v1/annotations')

  // handle redirect to next point
  useEffect(() => {
    const handleRedirect = (item: any, clickh: any, clickv: any) => {
      const ponitSelected = mapItems?.points?.find((point) => point.id === item)

      if (ponitSelected?.image360) {
        const krpano = document.getElementById('embedpano-viewer') as any
        Krpano.LoadCheckingViewerMode(() => {
          const h = krpano?.get('view.hlookat')
          const v = krpano?.get('view.vlookat')
          const z = krpano?.get('view.fov')

          const urlToUpdate = new URL(window.location.href)
          if (urlH) urlToUpdate.searchParams.set('hlookat', h)
          if (urlV) urlToUpdate.searchParams.set('vlookat', v)
          if (urlZ) urlToUpdate.searchParams.set('fov', z)

          // statis fpr display all anotation or hidden all anotation
          const isDisplay = urlToUpdate.searchParams.get('is_display')

          if (isDisplay) {
            urlToUpdate.searchParams.set('is_display', isDisplay)
          }

          window.history.pushState({}, '', urlToUpdate.toString())

          krpano.call(`
            set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, ${
              currentHgap - Number(ponitSelected.initView.radarHGap)
            }, 0.0, 0.9, easeoutquad);
            `)

          setCurrentPov({
            h: Number(h),
            v: Number(v),
            z
          })
          setCurrentViewer((prev) => {
            return {
              ...prev,
              selectedImage: ponitSelected.image360?.id,
              selectedPoint: ponitSelected.id,
              selectedArea: `${ponitSelected.areaId}`,
              isClickToDirection: true,
              isClickToNextFloor: true,
              isSelectPointInLeftMenu: false
            }
          })
        })
      } else {
        notification.error({
          key: 'redirect',
          message: '画像が設定されていません'
        })
      }
    }

    // Only set the function if it doesn't exist or if dependencies changed
    const depsKey = `${mapItems?.points?.length}-${currentHgap}`
    if (
      !(window as any).handle_redirect ||
      (window as any).handle_redirect.depsKey !== depsKey
    ) {
      ;(window as any).handle_redirect = handleRedirect
      ;(window as any).handle_redirect.depsKey = depsKey
    }

    return () => {
      // Only cleanup if component unmounts, not on every re-render
      if ((window as any).handle_redirect?.depsKey === depsKey) {
        delete (window as any).handle_redirect
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapItems?.points?.length, currentHgap])

  // handle click annotation
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annotationId: string) => {
      !isMutating &&
        trigger(
          {
            method: 'get',
            data: {
              building_id: id,
              floor_id: currentViewer.selectedFloor,
              point_id: currentViewer.selectedPoint
            },
            query: [annotationId]
          },
          {
            onSuccess: (data) => {
              setCurrentSelected(data)
            },
            onError: (error) => {
              setCurrentSelected((prev) => ({
                ...prev,
                isMutating: false
              }))
              if (error.response.data.error.key === 'annotation_not_exists') {
                const krpano = document.getElementById(
                  'embedpano-viewer'
                ) as any
                krpano.call(`remove_annotation(${annotationId})`)
                notification.error({
                  message: t('translation.deletedAnnotationNotice')
                })
              }
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
    }
  }

  // handle change floor
  const handleChangeFloor = () => {
    ;(window as any).handle_change_view_floor = (
      stair_id: string,
      targetFloor: string,
      targetPoint: string,
      clickh: any,
      clickv: any
    ) => {
      const embedpano = document.getElementById('embedpano-viewer') as any
      if (!building?.floors.find((item) => item.id === Number(targetFloor))) {
        notification.error({
          key: 'stair_error',
          message: t('building.destinationNotFound')
        })
        embedpano.call(`removehotspot('stair-${stair_id}')`)
        return
      }

      const h = embedpano?.get('view.hlookat')
      const v = embedpano?.get('view.vlookat')
      const z = embedpano?.get('view.fov')

      const urlToUpdate = new URL(window.location.href)
      urlToUpdate.searchParams.delete('floor')
      urlToUpdate.searchParams.delete('point')
      urlToUpdate.searchParams.delete('area')

      window.history.pushState({}, '', urlToUpdate.toString())

      embedpano.call(`
        if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
        set3dtransition("sphere", ${clickh}, ${clickv}, (min(1000,200)), 0, 0, 0.0, 0.9, easeoutquad);
      `)

      setCurrentPov({
        h: Number(h),
        v: Number(v),
        z
      })

      setCurrentViewer((prev) => ({
        ...prev,
        selectedFloor: Number(targetFloor),
        selectedPoint: targetPoint,
        isClickToDirection: true,
        isSelectPointInLeftMenu: false,
        isClickToNextFloor: true
      }))
    }
  }

  const addPropertiesToKrpano = useCallback(
    (
      krpano: any,
      annotations?: Annotation[],
      directions?: Direction[],
      stairways?: Stairway[]
    ) => {
      annotations &&
        Krpano.AddAnnotation(krpano, annotations, isAnnotationDisplay)
      directions && Krpano.AddDirectionArrow(krpano, directions)
      stairways && Krpano.AddStairway(krpano, stairways)
      if (currentViewer.isClickViewListFile) {
        setCurrentViewer((prev) => ({
          ...prev,
          isClickViewListFile: false
        }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAnnotationDisplay]
  )

  const krpanoOnreadyCallback = async (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[]
  ) => {
    const { h, v, z, previewUrl, tilesUrl, multires, imageUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true}`
    const currentH = isFirstLoading.current && urlH ? urlH : h
    const currentV = isFirstLoading.current && urlV ? urlV : v
    const currentZ = isFirstLoading.current && urlZ ? urlZ : z

    krpano.call(
      `loadpano('/krpano/xml-assets/pano_view.xml','${varString}',IGNOREKEEP,BLEND(1.0));`
    )

    const sceneName = Krpano.getScenceNameByFileId(
      currentViewer.selectedPoint,
      points,
      undefined
    )

    const sceneString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })

    krpano.call(`includexmlstring(${sceneString}, null, null, false)`)

    krpano.call(
      `load_scene(${sceneName},${currentH},${currentV},${currentZ},${!isFirstLoading.current})`
    )

    if (!isFirstLoading.current || currentViewer.isClickViewListFile) {
      addPropertiesToKrpano(krpano, annotations, directions, stairways)
    }

    isFirstLoading.current = false
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    pointDetail?: Point
  ) => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const target = document.getElementById('viewer-pano-container')
    let currentSelectedPoint = currentViewer.selectedPoint
    let currentSelectedImage = currentViewer.selectedImage
    if (pointDetail) {
      currentSelectedImage = pointDetail.image360?.id
      currentSelectedPoint = pointDetail.id
    }
    const { imageUrl, previewUrl, multires, tilesUrl, h, v, z } =
      krpanoInitProps

    if (target) {
      // fist time only whne krpano not mount -> init krpano with current point and init properties
      if (!embedpano) {
        ;(window as any).embedpano({
          id: 'embedpano-viewer',
          xml: null,
          target: 'viewer-pano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directions,
              stairways
            )
          }
        })
      } else {
        if (!currentViewer.isSelectPointInLeftMenu) {
          // call when krpano mounted and change point or click to direction
          const sceneName = Krpano.getScenceNameByFileId(
            currentSelectedPoint,
            points,
            currentSelectedImage
          )

          if (embedpano.get(`scene[${sceneName}]`) === null) {
            const sceneString = Krpano.generateSceneXML({
              sceneName,
              tilesUrl: tilesUrl || undefined,
              multires: multires || undefined,
              s3Path: imageUrl,
              thumbnailUrl: previewUrl || ''
            })

            embedpano.call(
              `includexmlstring(${sceneString}, null, null, false)`
            )
          }
          // load init view
          embedpano.call(`load_scene(${sceneName},${h},${v},${z})`)
        }
        embedpano.call(`remove_all_hotspot()`)
        addPropertiesToKrpano(embedpano, annotations, directions, stairways)
      }
    }
  }

  const handlePreloadScenes = async () => {
    const embedpano = document.getElementById('embedpano-viewer') as any
    const listPointTo = points
      .find((item) => item.id === currentViewer.selectedPoint)
      ?.directions?.map((item) => item.point_to)

    if (!listPointTo?.length) {
      return
    }

    await getSenceData(
      {
        method: 'post',
        data: {
          building_id: Number(id),
          floor_id: Number(currentViewer.selectedFloor),
          point_ids: listPointTo
        }
      },
      {
        onSuccess: (data: SenceDataResponse) => {
          try {
            const currentFileId = points.find(
              (p) => p.id === currentViewer.selectedPoint
            )?.image360?.id

            const items: SenceData[] = []

            data.data.forEach((item) => {
              if (
                embedpano.get(`scene[scene_${item.id}]`) === null &&
                item.id !== currentFileId
              ) {
                items.push(item)
              }
            })
            if (items.length) {
              const screenString = Krpano.generateSceneXMLs(items)
              embedpano.call(
                `includexmlstring(${screenString}, null, null, false)`
              )
            }
          } catch (error) {}
        },
        onError: () => {}
      }
    )
  }

  // get point detail when select point
  useSWR<Point>(
    currentViewer.selectedFloor &&
      currentViewer.selectedPoint &&
      building &&
      mapItems &&
      !isValidatingMapItems
      ? {
          url: `v1/points/${currentViewer.selectedPoint}`,
          params: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      onSuccess: (data) => {
        setCurrentViewer((prev) => {
          return {
            ...prev,
            pointDetail: data,
            selectedImage: data.image360?.id
          }
        })
        if (data?.image360) {
          let pov
          if (currentPov) {
            pov = currentPov
          } else if (urlH && urlV && urlZ) {
            pov = { h: +urlH, v: +urlV, z: +urlZ }
          } else {
            pov = data.initView
          }
          const initProps = {
            imageUrl: data.image360?.s3Path,
            previewUrl: data.image360?.multires
              ? `${data.image360?.tilesUrl}preview.jpg`
              : data.image360?.thumbnail,
            hgap: data?.initView.radarHGap,
            h: pov.h,
            v: pov.v,
            z: pov.z,
            multires: data.image360.multires,
            tilesUrl: data.image360.tilesUrl
          }

          setCurrentHgap(initProps.hgap)
          const annotationList = data.annotations?.filter(
            (item) => !item.imageID || item.imageID === data.image360?.id
          )

          const stairList = data.stairways?.map((item) => ({
            ...item,
            nextFloorName: building?.floors.find(
              (floor) => floor.id === +item.nextFloorId
            )?.name
          }))

          embedpano(initProps, annotationList, data.directions, stairList, data)
          handlePreloadScenes()
        } else {
          Krpano.UnmountPanoViewer()
        }
      }
    }
  )

  // handle event view around
  handleClickAnnotation()
  handleChangeFloor()

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoViewer()
    }
  }, [])

  return {
    currentViewer,
    setCurrentViewer,
    currentPov,
    setCurrentHgap,
    embedpano
  }
}
